// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/mission-control/PriorityAttentionPanel.jsx

import React from "react";
import { Button } from "../../design-system/components";
import MissionControlSection from "./MissionControlSection";
import MissionControlStatus from "./MissionControlStatus";

function metricTone(value, attentionTone = "medium") {
  return Number(value) > 0 ? attentionTone : "low";
}

function SopMetric({
  label,
  value,
  detail,
  tone,
  onClick,
}) {
  return (
    <button
      type="button"
      className={`priority-attention-sop priority-attention-sop--${tone}`}
      onClick={onClick}
      title={detail}
    >
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </button>
  );
}

export default function PriorityAttentionPanel({
  actions = [],
  sopMetrics = {},
  onExplainAction,
  onOpenAction,
  onOpenSop,
}) {
  const visibleActions = actions.slice(0, 4);
  const actionCount = actions.length;

  return (
    <MissionControlSection
      eyebrow="Priority attention"
      title="Move from signal to governed work"
      description="The most important operational actions and SOP obligations requiring review."
      className="priority-attention-panel"
      actions={
        <MissionControlStatus
          tone={actionCount > 0 ? "medium" : "low"}
        >
          {actionCount > 0
            ? `${actionCount} action${actionCount === 1 ? "" : "s"}`
            : "Clear"}
        </MissionControlStatus>
      }
    >
      <div className="priority-attention-panel__layout">
        <div className="priority-attention-panel__actions">
          <div className="priority-attention-panel__heading">
            <div>
              <h3>Priority Actions</h3>
              <p>Highest-value next steps from current operational context.</p>
            </div>
          </div>

          <div className="priority-attention-list">
            {visibleActions.length === 0 ? (
              <div className="priority-attention-list__empty">
                No priority actions are currently available.
              </div>
            ) : (
              visibleActions.map((action, index) => (
                <article
                  key={action.actionKey || `${action.title}-${index}`}
                  className="priority-attention-item"
                >
                  <div className="priority-attention-item__content">
                    <strong>{action.title}</strong>
                    <span>{action.detail}</span>
                  </div>

                  <div className="priority-attention-item__actions">
                    {action.explainAvailable !== false ? (
                      <Button
                        variant="ghost"
                        size="small"
                        onClick={() => onExplainAction?.(action)}
                        className="priority-attention-item__explain"
                      >
                        Explain
                      </Button>
                    ) : null}

                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => onOpenAction(action)}
                      className="priority-attention-item__button"
                    >
                      {action.cta}
                    </Button>
                  </div>
                </article>
              ))
            )}
          </div>
        </div>

        <div className="priority-attention-panel__sop">
          <div className="priority-attention-panel__heading priority-attention-panel__heading--sop">
            <div>
              <h3>SOP Center</h3>
              <p>Approval, freshness, and finding-linked procedure readiness.</p>
            </div>
            <Button
              variant="secondary"
              size="small"
              onClick={() => onOpenSop()}
              className="priority-attention-panel__sop-button"
            >
              Open SOP Center
            </Button>
          </div>

          <div className="priority-attention-sop-grid">
            <SopMetric
              label="Overdue SOPs"
              value={sopMetrics.overdueSops || 0}
              detail={sopMetrics.overdueFootnote || "Overdue or outdated procedures."}
              tone={metricTone(sopMetrics.overdueSops, "high")}
              onClick={() => onOpenSop("overdue")}
            />
            <SopMetric
              label="Drafts in review"
              value={sopMetrics.pendingReviewCount || 0}
              detail={sopMetrics.pendingReviewFootnote || "Draft procedures awaiting approval."}
              tone={metricTone(sopMetrics.pendingReviewCount, "medium")}
              onClick={() => onOpenSop("pending_approval")}
            />
            <SopMetric
              label="Finding-linked SOPs"
              value={
                sopMetrics.activeFindingSops === null ||
                sopMetrics.activeFindingSops === undefined
                  ? "—"
                  : sopMetrics.activeFindingSops
              }
              detail="Finding-linked SOP coverage becomes authoritative when active finding linkage is available."
              tone="neutral"
              onClick={() => onOpenSop()}
            />
          </div>
        </div>
      </div>
    </MissionControlSection>
  );
}
